import {
  CURRENT_SUPPLIER_STORAGE_KEY,
  SUPPLIER_ID_HEADER,
  SUPPLIER_OPTION_STORAGE_KEY,
} from "@/app-constants/supplierContext";

/** A supplier the user is an active user of, as returned by `SupplierUser/GetSupplierInfo`. */
export interface ISupplierInfo {
  supplierId: string;
  supplierNumber?: string;
  name?: string;
  userRights?: number | null;
  [key: string]: unknown;
}

const hasWindow = () => typeof window !== "undefined";

/** Id of the supplier stored as the current selection, or null when there is none or it is unreadable. */
export const readCurrentSupplierId = (): string | null => {
  if (!hasWindow()) return null;
  try {
    const supplierId = JSON.parse(
      window.localStorage.getItem(CURRENT_SUPPLIER_STORAGE_KEY) ?? "null",
    )?.supplierId;
    return typeof supplierId === "string" && supplierId ? supplierId : null;
  } catch {
    return null;
  }
};

/**
 * Stores the selection under both keys, in the shapes the header form uses: the full supplier, and the
 * dropdown option its supplier dropdown reads as its default value.
 */
export const storeCurrentSupplier = (supplier: ISupplierInfo): void => {
  window.localStorage.setItem(CURRENT_SUPPLIER_STORAGE_KEY, JSON.stringify(supplier));
  window.localStorage.setItem(
    SUPPLIER_OPTION_STORAGE_KEY,
    JSON.stringify({ label: supplier.name, value: supplier.supplierId }),
  );
};

export const clearCurrentSupplier = (): void => {
  if (!hasWindow()) return;
  window.localStorage.removeItem(CURRENT_SUPPLIER_STORAGE_KEY);
  window.localStorage.removeItem(SUPPLIER_OPTION_STORAGE_KEY);
};

/**
 * Keeps the stored selection when the user still belongs to that supplier, otherwise falls back to the
 * first one. This also drops a selection left behind by a previous user of the same browser.
 */
export const selectSupplier = (
  suppliers: ISupplierInfo[],
  storedSupplierId: string | null,
): ISupplierInfo | null =>
  suppliers.find((s) => s.supplierId === storedSupplierId) ?? suppliers[0] ?? null;

/**
 * `buildHttpRequestHeaders` for `ShaApplicationProvider`. Shesha does not call it per request: it merges
 * the result into the cached `httpHeaders` whenever `setRequestHeaders` runs, which is why
 * `SupplierContextProvider` calls `setRequestHeaders` after every change of selection.
 *
 * Returns nothing when no supplier is selected. Shesha merges into the previous headers, so an old value
 * stays until the next selection replaces it; that is harmless because the server re-checks membership
 * of the logged-in user on every request.
 */
export const buildSupplierHeaders = (): Record<string, string> => {
  const supplierId = readCurrentSupplierId();
  return supplierId ? { [SUPPLIER_ID_HEADER]: supplierId } : {};
};
